import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories, allArticles, faqData, heroSlides } from "@/mocks/siteData";
import ArticleCard from "@/components/feature/ArticleCard";
import SEOHead from "@/components/base/SEOHead";

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const featuredArticles = allArticles.slice(0, 6);

  const colorMap: Record<string, string> = {
    accent: "bg-accent-100 text-accent-800",
    primary: "bg-primary-100 text-primary-800",
    secondary: "bg-secondary-100 text-secondary-800",
  };

  return (
    <>
      <SEOHead
        title="本子漫画·正版资源导航与创作指南 — 本子漫画爱好者必看"
        description="汇聚正版本子漫画资源推荐、风格解析、创作技巧与购买指南，帮助漫画迷快速找到优质内容，了解本子漫画的文化背景与欣赏要点，收藏必备。"
        keywords="本子漫画,本子漫画资源推荐,本子漫画创作教程,本子漫画风格解析,日系漫画本子,本子漫画购买指南,漫画同人志"
        canonicalPath="/"
        ogImage={heroSlides[0].imageUrl}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        {heroSlides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-700 ${
              idx === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.imageUrl}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          </div>
        ))}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 transition-all duration-500">
              {heroSlides[currentSlide].title}
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              {heroSlides[currentSlide].subtitle}
            </p>
            <Link
              to={heroSlides[currentSlide].link}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {heroSlides[currentSlide].cta}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                idx === currentSlide ? "bg-white w-8" : "bg-white/50"
              }`}
              aria-label={`切换到第${idx + 1}张`}
            />
          ))}
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">探索内容分类</h2>
            <p className="text-foreground-600 max-w-xl mx-auto">六大内容板块，全方位覆盖本子漫画的各个方面，助你快速找到感兴趣的内容</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-lg shrink-0 ${colorMap[cat.color]}`}>
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-base mb-1 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">{cat.description}</p>
                    <span className="text-xs text-foreground-500">{cat.articleCount} 篇文章</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">精选推荐</h2>
            <p className="text-foreground-600 max-w-xl mx-auto">编辑团队精心挑选的高品质文章，涵盖资源推荐、创作教程与深度解析</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => {
              const cat = categories.find((c) => c.id === article.categoryId);
              return (
                <ArticleCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  excerpt={article.excerpt}
                  categoryName={cat?.name || ""}
                  tags={article.tags}
                  author={article.author}
                  date={article.date}
                  readCount={article.readCount}
                  imageUrl={article.imageUrl}
                />
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/category/ziyuan-tuijian"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-300 text-foreground-700 rounded-full text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">常见问题</h2>
            <p className="text-foreground-600">关于本子漫画，你可能想知道的一切</p>
          </div>
          <div className="space-y-3">
            {faqData.map((faq, idx) => (
              <div key={idx} className="border border-background-200/70 rounded-lg overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                >
                  <span className="font-medium text-foreground-950 pr-4">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 shrink-0 ${
                      faqOpen === idx ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    faqOpen === idx ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.answer}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-20 bg-accent-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">准备好开始你的漫画之旅了吗？</h2>
          <p className="text-background-50/80 mb-8 max-w-lg mx-auto">
            无论你是想找资源、学创作还是了解文化，这里都有你想要的一切
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/chuangzuo-jiaocheng"
              className="px-6 py-3 bg-background-50 text-accent-600 rounded-full font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              开始学习创作
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 border border-background-50/40 text-background-50 rounded-full font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "本子漫画导航",
            url: typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ : "",
            description: "汇聚正版本子漫画资源推荐、风格解析、创作技巧与购买指南",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ + "/category/{search_term_string}" : "",
              },
              "query-input": "required name=search_term_string",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "本子漫画导航",
            url: typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ : "",
            description: "正版资源导航与创作指南",
            contactPoint: {
              "@type": "ContactPoint",
              email: "contact@honkomanga-nav.com",
              contactType: "customer service",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}